import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useAppContext } from '@/composables/useAppContext';
import { useIssuesGrid } from '@/composables/useIssuesGrid';
import { useIssueTotals } from '@/composables/useIssueTotals';
import { useDrawingCategoryChart } from '@/charts/configs/useDrawingCategoryChart';
import { useIssueCategoryChart } from '@/charts/configs/useIssueCategoryChart';
import { useIssueDueByGanttChart } from '@/charts/configs/useIssueDueByGanttChart';

export default () => {
  const store = useStore();
  const app = useAppContext();
  const filterContext = app.filterIssuesContext;
  const filterStatusHistoryContext = app.filterStatusHistoryContext;
  const chartFontColor = app.chartFontColor;
  const dataLoaded = app.dataLoaded;
  const conn = app.conn;
  const ganttConditions = ref([]);

  const drawingChart = useDrawingCategoryChart({ chartName: 'drawingStackedBarChart', filterContext, conn });
  const categoryChart = useIssueCategoryChart({ chartName: 'categoryStackedBarChart', filterContext, conn });

  // The Due By Gantt (package BaseGanttChart, IA_ABSORPTION_PLAN 13b): its SQL, its status picker
  // and the grid conditions of a selected row are the config's
  const {
    chart: ganttChart,
    statusOptions: ganttStatusOptions,
    selectedStatuses: ganttSelectedStatuses,
    ready: ganttReady,
    conditionsFor: ganttConditionsFor,
  } = useIssueDueByGanttChart({ chartName: 'issueDueByGantt', filterContext, conn, dataLoaded });

  // Use gantt conditions directly for the grid
  const gridConditions = computed(() => ganttConditions.value);

  // The issues grid (the package IssuesGrid since 2026-10-12): its data, report and link opener
  // are useIssuesGrid's — one bag of props; the Gantt's selection narrows it (`conditions`)
  const issuesGrid = useIssuesGrid({
    conn,
    filterContext,
    dataLoaded,
    settings: app.settings,
    buildMode: app.buildMode,
    logoUrl: app.logoUrl,
    duckdb: app.duckdb,
    conditions: gridConditions,
  });

  // The headline cards: the package TotalAndAssignedCards' spark row, fed by useIssueTotals (its
  // SQL, the weekly sparklines and the two "assigned to…" switch filters on both contexts).
  const {
    totalItems, totalAssignedToYourTeams, totalAssignedToYou, sparklines,
    filterByMyTeams, filterByMe, handleFilterChange,
  } = useIssueTotals({
    conn,
    filterContext,
    statusHistoryContext: filterStatusHistoryContext,
    userId: app.userId,
    dataLoaded,
  });

  onMounted(async () => {
    await store.commit('resetFilters');
  });

  // A selected Gantt row narrows the grid; a cleared selection (null) shows it whole
  const handleGanttTaskSelected = (task) => {
    ganttConditions.value = ganttConditionsFor(task);
  };

  return {
    dataLoaded,
    totalItems,
    totalAssignedToYourTeams,
    totalAssignedToYou,
    sparklines,
    filterByMyTeams,
    filterByMe,
    handleFilterChange,
    drawingChart,
    filterContext,
    chartFontColor,
    categoryChart,
    ganttChart,
    ganttStatusOptions,
    ganttSelectedStatuses,
    ganttReady,
    handleGanttTaskSelected,
    issuesGrid
  };
};
